// Tutorial game scenario - predefined game states
import type { GameState, Player } from '../types/game'
import { TUTORIAL_CARDS, ALICE_CARDS, BOB_CARDS } from './tutorialCards'

/**
 * Complete initial game state for the tutorial.
 * This is a valid GameState object that can be used directly.
 */
export const TUTORIAL_INITIAL_STATE: GameState = {
  players: [
    {
      id: 'player_0',
      name: 'You',
      money: 100,
      hand: [
        TUTORIAL_CARDS.yokoHidden,
        TUTORIAL_CARDS.sigridOneOffer,
        TUTORIAL_CARDS.manuelOpen,
        TUTORIAL_CARDS.danielFixed,
        TUTORIAL_CARDS.ramonDouble,
        TUTORIAL_CARDS.rafaelOpen,
      ],
      purchasedThisRound: [],
      purchases: [],
      isAI: false,
    },
    {
      id: 'player_1',
      name: 'Alice',
      money: 100,
      hand: ALICE_CARDS,
      purchasedThisRound: [],
      purchases: [],
      isAI: true,
      aiDifficulty: 'medium',
    },
    {
      id: 'player_2',
      name: 'Bob',
      money: 100,
      hand: BOB_CARDS,
      purchasedThisRound: [],
      purchases: [],
      isAI: true,
      aiDifficulty: 'medium',
    },
  ],

  deck: [], // Empty for tutorial - cards are pre-dealt

  discardPile: [],

  board: {
    artistValues: {
      'Solstice Vane': [0, 0, 0, 0],
      'Mira Faux': [0, 0, 0, 0],
      'Atlas Zero': [0, 0, 0, 0],
      'Vesper Kint': [0, 0, 0, 0],
      'Luxe Nebula': [0, 0, 0, 0],
    },
    playedCards: {
      'Solstice Vane': [],
      'Mira Faux': [],
      'Atlas Zero': [],
      'Vesper Kint': [],
      'Luxe Nebula': [],
    },
  },

  round: {
    roundNumber: 1,
    cardsPlayedPerArtist: {
      'Solstice Vane': 0,
      'Mira Faux': 0,
      'Atlas Zero': 0,
      'Vesper Kint': 0,
      'Luxe Nebula': 0,
    },
    currentAuctioneerIndex: 0,
    phase: {
      type: 'awaiting_card_play',
      activePlayerIndex: 0,
    },
  },

  gamePhase: 'playing',
  winner: null,
  eventLog: [],
}

/**
 * State after player plays the Solstice Vane hidden auction card.
 * Card moves from hand to auction area, auction starts.
 */
export const TUTORIAL_CARD_PLAYED_STATE: GameState = {
  players: [
    {
      id: 'player_0',
      name: 'You',
      money: 100,
      hand: [
        // yokoHidden removed - it's been played
        TUTORIAL_CARDS.sigridOneOffer,
        TUTORIAL_CARDS.manuelOpen,
        TUTORIAL_CARDS.danielFixed,
        TUTORIAL_CARDS.ramonDouble,
        TUTORIAL_CARDS.rafaelOpen,
      ],
      purchasedThisRound: [],
      purchases: [],
      isAI: false,
    },
    {
      id: 'player_1',
      name: 'Alice',
      money: 100,
      hand: ALICE_CARDS,
      purchasedThisRound: [],
      purchases: [],
      isAI: true,
      aiDifficulty: 'medium',
    },
    {
      id: 'player_2',
      name: 'Bob',
      money: 100,
      hand: BOB_CARDS,
      purchasedThisRound: [],
      purchases: [],
      isAI: true,
      aiDifficulty: 'medium',
    },
  ],

  deck: [],
  discardPile: [],

  board: {
    artistValues: {
      'Solstice Vane': [0, 0, 0, 0],
      'Mira Faux': [0, 0, 0, 0],
      'Atlas Zero': [0, 0, 0, 0],
      'Vesper Kint': [0, 0, 0, 0],
      'Luxe Nebula': [0, 0, 0, 0],
    },
    playedCards: {
      'Solstice Vane': [TUTORIAL_CARDS.yokoHidden],
      'Mira Faux': [],
      'Atlas Zero': [],
      'Vesper Kint': [],
      'Luxe Nebula': [],
    },
  },

  round: {
    roundNumber: 1,
    cardsPlayedPerArtist: {
      'Solstice Vane': 1,
      'Mira Faux': 0,
      'Atlas Zero': 0,
      'Vesper Kint': 0,
      'Luxe Nebula': 0,
    },
    currentAuctioneerIndex: 0,
    phase: {
      type: 'auction',
      auction: {
        type: 'hidden',
        card: TUTORIAL_CARDS.yokoHidden,
        auctioneerId: 'player_0',
        bids: {}, // Record<string, number>
        isActive: true,
        tieBreakOrder: ['player_0', 'player_1', 'player_2'],
        revealedBids: false,
      },
    },
  },

  gamePhase: 'playing',
  winner: null,
  eventLog: [],
}

/**
 * State after hidden auction completes with bids.
 * Shows the result of the auction.
 */
export const TUTORIAL_AUCTION_RESULT_STATE: GameState = {
  players: [
    {
      id: 'player_0',
      name: 'You',
      money: 92, // Paid 8 for the card
      hand: [
        TUTORIAL_CARDS.sigridOneOffer,
        TUTORIAL_CARDS.manuelOpen,
        TUTORIAL_CARDS.danielFixed,
        TUTORIAL_CARDS.ramonDouble,
        TUTORIAL_CARDS.rafaelOpen,
      ],
      purchasedThisRound: [TUTORIAL_CARDS.yokoHidden],
      purchases: [TUTORIAL_CARDS.yokoHidden],
      isAI: false,
    },
    {
      id: 'player_1',
      name: 'Alice',
      money: 100,
      hand: ALICE_CARDS,
      purchasedThisRound: [],
      purchases: [],
      isAI: true,
      aiDifficulty: 'medium',
    },
    {
      id: 'player_2',
      name: 'Bob',
      money: 100,
      hand: BOB_CARDS,
      purchasedThisRound: [],
      purchases: [],
      isAI: true,
      aiDifficulty: 'medium',
    },
  ],

  deck: [],
  discardPile: [],

  board: {
    artistValues: {
      'Solstice Vane': [0, 0, 0, 0],
      'Mira Faux': [0, 0, 0, 0],
      'Atlas Zero': [0, 0, 0, 0],
      'Vesper Kint': [0, 0, 0, 0],
      'Luxe Nebula': [0, 0, 0, 0],
    },
    playedCards: {
      'Solstice Vane': [TUTORIAL_CARDS.yokoHidden],
      'Mira Faux': [],
      'Atlas Zero': [],
      'Vesper Kint': [],
      'Luxe Nebula': [],
    },
  },

  round: {
    roundNumber: 1,
    cardsPlayedPerArtist: {
      'Solstice Vane': 1,
      'Mira Faux': 0,
      'Atlas Zero': 0,
      'Vesper Kint': 0,
      'Luxe Nebula': 0,
    },
    currentAuctioneerIndex: 0,
    phase: {
      type: 'awaiting_card_play',
      activePlayerIndex: 1, // Alice's turn next
    },
  },

  gamePhase: 'playing',
  winner: null,
  eventLog: [],
}

/**
 * State showing an open auction in progress.
 * Alice plays a card, open auction has started.
 */
export const TUTORIAL_OPEN_AUCTION_STATE: GameState = {
  players: [
    {
      id: 'player_0',
      name: 'You',
      money: 92,
      hand: [
        TUTORIAL_CARDS.sigridOneOffer,
        TUTORIAL_CARDS.manuelOpen,
        TUTORIAL_CARDS.danielFixed,
        TUTORIAL_CARDS.ramonDouble,
        TUTORIAL_CARDS.rafaelOpen,
      ],
      purchasedThisRound: [TUTORIAL_CARDS.yokoHidden],
      purchases: [TUTORIAL_CARDS.yokoHidden],
      isAI: false,
    },
    {
      id: 'player_1',
      name: 'Alice',
      money: 100,
      hand: ALICE_CARDS.filter(c => c.id !== 'alice_card_0'), // Played her card
      purchasedThisRound: [],
      purchases: [],
      isAI: true,
      aiDifficulty: 'medium',
    },
    {
      id: 'player_2',
      name: 'Bob',
      money: 100,
      hand: BOB_CARDS,
      purchasedThisRound: [],
      purchases: [],
      isAI: true,
      aiDifficulty: 'medium',
    },
  ],

  deck: [],
  discardPile: [],

  board: {
    artistValues: {
      'Solstice Vane': [0, 0, 0, 0],
      'Mira Faux': [0, 0, 0, 0],
      'Atlas Zero': [0, 0, 0, 0],
      'Vesper Kint': [0, 0, 0, 0],
      'Luxe Nebula': [0, 0, 0, 0],
    },
    playedCards: {
      'Solstice Vane': [TUTORIAL_CARDS.yokoHidden],
      'Mira Faux': [],
      'Atlas Zero': [],
      'Vesper Kint': [],
      'Luxe Nebula': [],
    },
  },

  round: {
    roundNumber: 1,
    cardsPlayedPerArtist: {
      'Solstice Vane': 1,
      'Mira Faux': 1,
      'Atlas Zero': 0,
      'Vesper Kint': 0,
      'Luxe Nebula': 0,
    },
    currentAuctioneerIndex: 1,
    phase: {
      type: 'auction',
      auction: {
        type: 'open',
        card: {
          id: 'alice_sigrid_card',
          artist: 'Mira Faux',
          auctionType: 'open',
          artworkId: 'sigrid_thaler_open_1',
          cardIndex: 1,
        },
        auctioneerId: 'player_1',
        currentBid: 15,
        currentBidderId: 'player_0',
        isActive: true,
        bidHistory: [
          { playerId: 'player_2', amount: 10, timestamp: Date.now() - 1000 },
          { playerId: 'player_0', amount: 15, timestamp: Date.now() },
        ],
        playerOrder: ['player_1', 'player_2', 'player_0'],
        currentPlayerIndex: 1,
        passCount: 0,
        timerEndTime: null,
        timerDuration: 10000,
      },
    },
  },

  gamePhase: 'playing',
  winner: null,
  eventLog: [],
}

/**
 * State showing round ending phase.
 */
export const TUTORIAL_ROUND_ENDING_STATE: GameState = {
  players: [
    {
      id: 'player_0',
      name: 'You',
      money: 70, // After buying another card
      hand: [
        TUTORIAL_CARDS.danielFixed,
        TUTORIAL_CARDS.ramonDouble,
      ],
      purchasedThisRound: [TUTORIAL_CARDS.yokoHidden, TUTORIAL_CARDS.manuelOpen],
      purchases: [TUTORIAL_CARDS.yokoHidden, TUTORIAL_CARDS.manuelOpen],
      isAI: false,
    },
    {
      id: 'player_1',
      name: 'Alice',
      money: 85,
      hand: [],
      purchasedThisRound: [TUTORIAL_CARDS.sigridOneOffer],
      purchases: [TUTORIAL_CARDS.sigridOneOffer],
      isAI: true,
      aiDifficulty: 'medium',
    },
    {
      id: 'player_2',
      name: 'Bob',
      money: 90,
      hand: [],
      purchasedThisRound: [],
      purchases: [],
      isAI: true,
      aiDifficulty: 'medium',
    },
  ],

  deck: [],
  discardPile: [],

  board: {
    artistValues: {
      'Solstice Vane': [0, 0, 0, 0],
      'Mira Faux': [0, 0, 0, 0],
      'Atlas Zero': [0, 0, 0, 0],
      'Vesper Kint': [0, 0, 0, 0],
      'Luxe Nebula': [0, 0, 0, 0],
    },
    playedCards: {
      'Solstice Vane': [TUTORIAL_CARDS.yokoHidden],
      'Mira Faux': [],
      'Atlas Zero': [],
      'Vesper Kint': [TUTORIAL_CARDS.ramonDouble],
      'Luxe Nebula': [],
    },
  },

  round: {
    roundNumber: 1,
    cardsPlayedPerArtist: {
      'Solstice Vane': 1,
      'Mira Faux': 1,
      'Atlas Zero': 0,
      'Vesper Kint': 1,
      'Luxe Nebula': 0,
    },
    currentAuctioneerIndex: 0,
    phase: {
      type: 'round_ending',
      unsoldCards: [TUTORIAL_CARDS.rafaelOpen],
    },
  },

  gamePhase: 'playing',
  winner: null,
  eventLog: [],
}

/**
 * State showing artist valuation (prices being set).
 */
export const TUTORIAL_VALUATION_STATE: GameState = {
  players: [
    {
      id: 'player_0',
      name: 'You',
      money: 70,
      hand: [
        TUTORIAL_CARDS.danielFixed,
        TUTORIAL_CARDS.ramonDouble,
      ],
      purchasedThisRound: [TUTORIAL_CARDS.yokoHidden, TUTORIAL_CARDS.manuelOpen],
      purchases: [TUTORIAL_CARDS.yokoHidden, TUTORIAL_CARDS.manuelOpen],
      isAI: false,
    },
    {
      id: 'player_1',
      name: 'Alice',
      money: 85,
      hand: [],
      purchasedThisRound: [TUTORIAL_CARDS.sigridOneOffer],
      purchases: [TUTORIAL_CARDS.sigridOneOffer],
      isAI: true,
      aiDifficulty: 'medium',
    },
    {
      id: 'player_2',
      name: 'Bob',
      money: 90,
      hand: [],
      purchasedThisRound: [],
      purchases: [],
      isAI: true,
      aiDifficulty: 'medium',
    },
  ],

  deck: [],
  discardPile: [TUTORIAL_CARDS.rafaelOpen],

  board: {
    artistValues: {
      'Solstice Vane': [30, 0, 0, 0], // 1 card sold for 30
      'Mira Faux': [20, 0, 0, 0],  // 1 card sold for 20
      'Atlas Zero': [0, 0, 0, 0],
      'Vesper Kint': [0, 0, 0, 0], // Double card - no value yet
      'Luxe Nebula': [0, 0, 0, 0],
    },
    playedCards: {
      'Solstice Vane': [TUTORIAL_CARDS.yokoHidden],
      'Mira Faux': [],
      'Atlas Zero': [],
      'Vesper Kint': [TUTORIAL_CARDS.ramonDouble],
      'Luxe Nebula': [],
    },
  },

  round: {
    roundNumber: 1,
    cardsPlayedPerArtist: {
      'Solstice Vane': 1,
      'Mira Faux': 1,
      'Atlas Zero': 0,
      'Vesper Kint': 1,
      'Luxe Nebula': 0,
    },
    currentAuctioneerIndex: 0,
    phase: {
      type: 'selling_to_bank',
      results: [], // Valuation results
    },
  },

  gamePhase: 'playing',
  winner: null,
  eventLog: [],
}

/**
 * State showing round complete with scoring.
 */
export const TUTORIAL_ROUND_COMPLETE_STATE: GameState = {
  players: [
    {
      id: 'player_0',
      name: 'You',
      money: 70 + 60, // Money + painting value (2 paintings × $30k each = $60k)
      hand: [
        TUTORIAL_CARDS.danielFixed,
        TUTORIAL_CARDS.ramonDouble,
      ],
      purchasedThisRound: [],
      purchases: [TUTORIAL_CARDS.yokoHidden, TUTORIAL_CARDS.manuelOpen],
      isAI: false,
    },
    {
      id: 'player_1',
      name: 'Alice',
      money: 85 + 20,
      hand: [],
      purchasedThisRound: [],
      purchases: [TUTORIAL_CARDS.sigridOneOffer],
      isAI: true,
      aiDifficulty: 'medium',
    },
    {
      id: 'player_2',
      name: 'Bob',
      money: 90,
      hand: [],
      purchasedThisRound: [],
      purchases: [],
      isAI: true,
      aiDifficulty: 'medium',
    },
  ],

  deck: [],
  discardPile: [TUTORIAL_CARDS.rafaelOpen],

  board: {
    artistValues: {
      'Solstice Vane': [30, 0, 0, 0],
      'Mira Faux': [20, 0, 0, 0],
      'Atlas Zero': [0, 0, 0, 0],
      'Vesper Kint': [0, 0, 0, 0],
      'Luxe Nebula': [0, 0, 0, 0],
    },
    playedCards: {
      'Solstice Vane': [TUTORIAL_CARDS.yokoHidden],
      'Mira Faux': [],
      'Atlas Zero': [],
      'Vesper Kint': [TUTORIAL_CARDS.ramonDouble],
      'Luxe Nebula': [],
    },
  },

  round: {
    roundNumber: 2, // Round 2 now!
    cardsPlayedPerArtist: {
      'Solstice Vane': 0,
      'Mira Faux': 0,
      'Atlas Zero': 0,
      'Vesper Kint': 0,
      'Luxe Nebula': 0,
    },
    currentAuctioneerIndex: 1,
    phase: {
      type: 'awaiting_card_play',
      activePlayerIndex: 1,
    },
  },

  gamePhase: 'playing',
  winner: null,
  eventLog: [],
}

/**
 * State showing Round 2 in progress with accumulated values.
 * Manuel is still valuable, Sigrid is second, player is buying strategically.
 */
export const TUTORIAL_ROUND_2_PLAYING: GameState = {
  players: [
    {
      id: 'player_0',
      name: 'You',
      money: 130, // Round 1 payout: started with 70, sold 2 Manuel × $30 = +$60
      hand: [
        TUTORIAL_CARDS.danielFixed,
        TUTORIAL_CARDS.ramonDouble,
      ],
      purchasedThisRound: [],
      purchases: [], // Previous paintings sold to bank
      isAI: false,
    },
    {
      id: 'player_1',
      name: 'Alice',
      money: 105, // 85 + sold her Sigrid painting ($20)
      hand: [],
      purchasedThisRound: [],
      purchases: [],
      isAI: true,
      aiDifficulty: 'medium',
    },
    {
      id: 'player_2',
      name: 'Bob',
      money: 90,
      hand: [],
      purchasedThisRound: [],
      purchases: [],
      isAI: true,
      aiDifficulty: 'medium',
    },
  ],

  deck: [],
  discardPile: [TUTORIAL_CARDS.rafaelOpen, TUTORIAL_CARDS.yokoHidden, TUTORIAL_CARDS.manuelOpen, TUTORIAL_CARDS.sigridOneOffer],

  board: {
    artistValues: {
      'Solstice Vane': [30, 0, 0, 0], // Still worth $30k from Round 1
      'Mira Faux': [20, 0, 0, 0],
      'Atlas Zero': [0, 0, 0, 0],
      'Vesper Kint': [0, 0, 0, 0],
      'Luxe Nebula': [0, 0, 0, 0],
    },
    playedCards: {
      'Solstice Vane': [],
      'Mira Faux': [],
      'Atlas Zero': [],
      'Vesper Kint': [],
      'Luxe Nebula': [],
    },
  },

  round: {
    roundNumber: 2,
    cardsPlayedPerArtist: {
      'Solstice Vane': 0,
      'Mira Faux': 0,
      'Atlas Zero': 0,
      'Vesper Kint': 0,
      'Luxe Nebula': 0,
    },
    currentAuctioneerIndex: 0,
    phase: {
      type: 'awaiting_card_play',
      activePlayerIndex: 0,
    },
  },

  gamePhase: 'playing',
  winner: null,
  eventLog: [],
}

/**
 * State showing Round 2 valuation with value accumulation.
 * Manuel places 2nd, Sigrid places 1st - values accumulate!
 */
export const TUTORIAL_ROUND_2_VALUATION: GameState = {
  players: [
    {
      id: 'player_0',
      name: 'You',
      money: 78, // After buying some paintings
      hand: [],
      purchasedThisRound: [TUTORIAL_CARDS.danielFixed, TUTORIAL_CARDS.ramonDouble],
      purchases: [TUTORIAL_CARDS.danielFixed, TUTORIAL_CARDS.ramonDouble],
      isAI: false,
    },
    {
      id: 'player_1',
      name: 'Alice',
      money: 95,
      hand: [],
      purchasedThisRound: [],
      purchases: [],
      isAI: true,
      aiDifficulty: 'medium',
    },
    {
      id: 'player_2',
      name: 'Bob',
      money: 90,
      hand: [],
      purchasedThisRound: [],
      purchases: [],
      isAI: true,
      aiDifficulty: 'medium',
    },
  ],

  deck: [],
  discardPile: [],

  board: {
    artistValues: {
      'Solstice Vane': [30, 20, 0, 0], // 1st in R1 ($30k) + 2nd in R2 ($20k) = $50k each!
      'Mira Faux': [20, 30, 0, 0],  // 2nd in R1 ($20k) + 1st in R2 ($30k) = $50k each!
      'Atlas Zero': [0, 10, 0, 0],    // 3rd in R2 = $10k (CORRECTED)
      'Vesper Kint': [0, 0, 0, 0],    // Nothing in R2 = $0 (CORRECTED)
      'Luxe Nebula': [0, 0, 0, 0],
    },
    playedCards: {
      'Solstice Vane': [],
      'Mira Faux': [],
      'Atlas Zero': [],
      'Vesper Kint': [],
      'Luxe Nebula': [],
    },
  },

  round: {
    roundNumber: 2,
    cardsPlayedPerArtist: {
      'Solstice Vane': 2,
      'Mira Faux': 3,
      'Atlas Zero': 2,
      'Vesper Kint': 1,
      'Luxe Nebula': 0,
    },
    currentAuctioneerIndex: 1,
    phase: {
      type: 'selling_to_bank',
      results: [],
    },
  },

  gamePhase: 'playing',
  winner: null,
  eventLog: [],
}

/**
 * State showing Round 2 complete - players get paid and Round 3 begins.
 */
export const TUTORIAL_ROUND_2_COMPLETE: GameState = {
  players: [
    {
      id: 'player_0',
      name: 'You',
      money: 138, // 130 - 22 (spent on danielFixed $12 + ramonDouble $10) + 20 (sold danielFixed $10)
      hand: [
        TUTORIAL_CARDS.rafaelOpen, // Dealt a new card for Round 3
      ],
      purchasedThisRound: [],
      purchases: [TUTORIAL_CARDS.danielFixed, TUTORIAL_CARDS.ramonDouble], // Sold to bank
      isAI: false,
    },
    {
      id: 'player_1',
      name: 'Alice',
      money: 145, // 105 + 40 (sold 2 Sigrid paintings at new values)
      hand: [],
      purchasedThisRound: [],
      purchases: [],
      isAI: true,
      aiDifficulty: 'medium',
    },
    {
      id: 'player_2',
      name: 'Bob',
      money: 140, // 90 + 50 (sold his Manuel paintings)
      hand: [],
      purchasedThisRound: [],
      purchases: [],
      isAI: true,
      aiDifficulty: 'medium',
    },
  ],

  deck: [],
  discardPile: [TUTORIAL_CARDS.danielFixed, TUTORIAL_CARDS.ramonDouble],

  board: {
    artistValues: {
      'Solstice Vane': [30, 20, 0, 0], // R1:1st($30) + R2:2nd($20) = $50k each
      'Mira Faux': [20, 30, 0, 0],  // R1:2nd($20) + R2:1st($30) = $50k each
      'Atlas Zero': [0, 10, 0, 0],    // R2:3rd($10) = $10k each
      'Vesper Kint': [0, 0, 0, 0],    // Never placed = $0 each
      'Luxe Nebula': [0, 0, 0, 0],
    },
    playedCards: {
      'Solstice Vane': [],
      'Mira Faux': [],
      'Atlas Zero': [],
      'Vesper Kint': [],
      'Luxe Nebula': [],
    },
  },

  round: {
    roundNumber: 3, // Round 3 now!
    cardsPlayedPerArtist: {
      'Solstice Vane': 0,
      'Mira Faux': 0,
      'Atlas Zero': 0,
      'Vesper Kint': 0,
      'Luxe Nebula': 0,
    },
    currentAuctioneerIndex: 0,
    phase: {
      type: 'awaiting_card_play',
      activePlayerIndex: 0,
    },
  },

  gamePhase: 'playing',
  winner: null,
  eventLog: [],
}

/**
 * State showing Round 3 valuation - THREE artists place this round!
 * This shows a realistic round with competition. Daniel and Ramon didn't place.
 */
export const TUTORIAL_ROUND_3_VALUATION: GameState = {
  players: [
    {
      id: 'player_0',
      name: 'You',
      money: 118, // 138 - 20 (bought rafaelOpen for $20)
      hand: [],
      purchasedThisRound: [TUTORIAL_CARDS.rafaelOpen],
      purchases: [TUTORIAL_CARDS.danielFixed, TUTORIAL_CARDS.ramonDouble, TUTORIAL_CARDS.rafaelOpen],
      isAI: false,
    },
    {
      id: 'player_1',
      name: 'Alice',
      money: 125, // 145 - 20 (bought more paintings)
      hand: [],
      purchasedThisRound: [],
      purchases: [],
      isAI: true,
      aiDifficulty: 'medium',
    },
    {
      id: 'player_2',
      name: 'Bob',
      money: 120, // 140 - 20 (bought more paintings)
      hand: [],
      purchasedThisRound: [],
      purchases: [],
      isAI: true,
      aiDifficulty: 'medium',
    },
  ],

  deck: [],
  discardPile: [TUTORIAL_CARDS.rafaelOpen],

  board: {
    artistValues: {
      'Solstice Vane': [30, 20, 30, 0], // R1:1st($30) + R2:2nd($20) + R3:1st($30) = $80k each
      'Mira Faux': [20, 30, 20, 0],  // R1:2nd($20) + R2:1st($30) + R3:2nd($20) = $70k each
      'Atlas Zero': [0, 10, 0, 0],     // R2:3rd($10) + R3:nothing = still $10k
      'Vesper Kint': [0, 0, 0, 0],     // Never placed = $0
      'Luxe Nebula': [0, 0, 10, 0],  // R3:3rd($10) = $10k each
    },
    playedCards: {
      'Solstice Vane': [],
      'Mira Faux': [],
      'Atlas Zero': [],
      'Vesper Kint': [],
      'Luxe Nebula': [],
    },
  },

  round: {
    roundNumber: 3,
    cardsPlayedPerArtist: {
      'Solstice Vane': 3,
      'Mira Faux': 2,
      'Atlas Zero': 0,
      'Vesper Kint': 0,
      'Luxe Nebula': 1,
    },
    currentAuctioneerIndex: 1,
    phase: {
      type: 'selling_to_bank',
      results: [],
    },
  },

  gamePhase: 'playing',
  winner: null,
  eventLog: [],
}

/**
 * State showing Round 3 complete - players get paid and Round 4 begins.
 * All paintings have been sold to the bank at Round 3 accumulated values.
 */
export const TUTORIAL_ROUND_3_COMPLETE: GameState = {
  players: [
    {
      id: 'player_0',
      name: 'You',
      money: 188, // 118 + sold paintings at R3 values
      hand: [],
      purchasedThisRound: [],
      purchases: [], // Paintings sold to bank!
      isAI: false,
    },
    {
      id: 'player_1',
      name: 'Alice',
      money: 195, // 125 + sold her paintings ($70)
      hand: [],
      purchasedThisRound: [],
      purchases: [],
      isAI: true,
      aiDifficulty: 'medium',
    },
    {
      id: 'player_2',
      name: 'Bob',
      money: 200, // 120 + sold his Manuel paintings ($80)
      hand: [],
      purchasedThisRound: [],
      purchases: [],
      isAI: true,
      aiDifficulty: 'medium',
    },
  ],

  deck: [],
  discardPile: [TUTORIAL_CARDS.rafaelOpen, TUTORIAL_CARDS.danielFixed, TUTORIAL_CARDS.ramonDouble],

  board: {
    artistValues: {
      'Solstice Vane': [30, 20, 30, 0], // R1:1st($30) + R2:2nd($20) + R3:1st($30) = $80k each
      'Mira Faux': [20, 30, 20, 0],  // R1:2nd($20) + R2:1st($30) + R3:2nd($20) = $70k each
      'Atlas Zero': [0, 10, 0, 0],     // R2:3rd($10) = $10k each
      'Vesper Kint': [0, 0, 0, 0],     // Never placed = $0 each
      'Luxe Nebula': [0, 0, 10, 0],  // R3:3rd($10) = $10k each
    },
    playedCards: {
      'Solstice Vane': [],
      'Mira Faux': [],
      'Atlas Zero': [],
      'Vesper Kint': [],
      'Luxe Nebula': [],
    },
  },

  round: {
    roundNumber: 4, // Round 4 now!
    cardsPlayedPerArtist: {
      'Solstice Vane': 0,
      'Mira Faux': 0,
      'Atlas Zero': 0,
      'Vesper Kint': 0,
      'Luxe Nebula': 0,
    },
    currentAuctioneerIndex: 0,
    phase: {
      type: 'awaiting_card_play',
      activePlayerIndex: 0,
    },
  },

  gamePhase: 'playing',
  winner: null,
  eventLog: [],
}

/**
 * State showing Round 4 valuation - final round!
 * Sigrid wins it, Manuel 2nd, Rafael makes a comeback for 3rd.
 */
export const TUTORIAL_ROUND_4_VALUATION: GameState = {
  players: [
    {
      id: 'player_0',
      name: 'You',
      money: 158, // 188 - 30 (bought more paintings this round)
      hand: [],
      purchasedThisRound: [],
      purchases: [],
      isAI: false,
    },
    {
      id: 'player_1',
      name: 'Alice',
      money: 165, // 195 - 30 (bought more paintings)
      hand: [],
      purchasedThisRound: [],
      purchases: [],
      isAI: true,
      aiDifficulty: 'medium',
    },
    {
      id: 'player_2',
      name: 'Bob',
      money: 170, // 200 - 30 (bought more paintings)
      hand: [],
      purchasedThisRound: [],
      purchases: [],
      isAI: true,
      aiDifficulty: 'medium',
    },
  ],

  deck: [],
  discardPile: [],

  board: {
    artistValues: {
      'Solstice Vane': [30, 20, 30, 20], // R1:1st($30) + R2:2nd($20) + R3:1st($30) + R4:2nd($20) = $100k each!
      'Mira Faux': [20, 30, 20, 30],  // R1:2nd($20) + R2:1st($30) + R3:2nd($20) + R4:1st($30) = $100k each!
      'Atlas Zero': [0, 10, 0, 0],      // R2:3rd($10) + nothing = $10k each
      'Vesper Kint': [0, 0, 0, 0],      // Never placed = $0
      'Luxe Nebula': [0, 0, 10, 10],  // R3:3rd($10) + R4:3rd($10) = $20k each
    },
    playedCards: {
      'Solstice Vane': [],
      'Mira Faux': [],
      'Atlas Zero': [],
      'Vesper Kint': [],
      'Luxe Nebula': [],
    },
  },

  round: {
    roundNumber: 4,
    cardsPlayedPerArtist: {
      'Solstice Vane': 2,
      'Mira Faux': 2,
      'Atlas Zero': 0,
      'Vesper Kint': 0,
      'Luxe Nebula': 1,
    },
    currentAuctioneerIndex: 1,
    phase: {
      type: 'selling_to_bank',
      results: [],
    },
  },

  gamePhase: 'playing',
  winner: null,
  eventLog: [],
}

/**
 * Final state showing game complete with winner determination.
 * The player WINS the tutorial for a satisfying learning experience!
 */
export const TUTORIAL_GAME_COMPLETE: GameState = {
  players: [
    {
      id: 'player_0',
      name: 'You',
      money: 320, // Player wins! Bought smart paintings that accumulated value
      hand: [],
      purchasedThisRound: [],
      purchases: [],
      isAI: false,
    },
    {
      id: 'player_1',
      name: 'Alice',
      money: 245, // Alice did okay but focused on wrong artists
      hand: [],
      purchasedThisRound: [],
      purchases: [],
      isAI: true,
      aiDifficulty: 'medium',
    },
    {
      id: 'player_2',
      name: 'Bob',
      money: 220, // Bob bought poorly (Ramon at $0)
      hand: [],
      purchasedThisRound: [],
      purchases: [],
      isAI: true,
      aiDifficulty: 'medium',
    },
  ],

  deck: [],
  discardPile: [],

  board: {
    artistValues: {
      'Solstice Vane': [30, 20, 30, 20], // Final: $100k each
      'Mira Faux': [20, 30, 20, 30],  // Final: $100k each
      'Atlas Zero': [0, 10, 0, 0],      // Final: $10k each
      'Vesper Kint': [0, 0, 0, 0],      // Final: $0 each (never placed!)
      'Luxe Nebula': [0, 0, 10, 10],  // Final: $20k each
    },
    playedCards: {
      'Solstice Vane': [],
      'Mira Faux': [],
      'Atlas Zero': [],
      'Vesper Kint': [],
      'Luxe Nebula': [],
    },
  },

  round: {
    roundNumber: 4,
    cardsPlayedPerArtist: {
      'Solstice Vane': 0,
      'Mira Faux': 0,
      'Atlas Zero': 0,
      'Vesper Kint': 0,
      'Luxe Nebula': 0,
    },
    currentAuctioneerIndex: 0,
    phase: {
      type: 'awaiting_card_play',
      activePlayerIndex: 0,
    },
  },

  gamePhase: 'playing', // Still 'playing' in the game state type
  winner: null, // Winner is shown in tutorial tooltip, not game state
  eventLog: [],
}

